/**
 * Karte eines Hintergrundjobs im Chat (Resource-Typ `job`, tri-server backgroundJobs.ts).
 *
 * Zeigt, was läuft: Titel, Zustand, die letzten Schritte und die Laufzeit; erlaubt den Abbruch.
 * Fragt alle 4 s nach, solange der Job läuft. Ist er fertig, lädt das offene Gespräch neu — die
 * Antwort mit Register, Losen und GAEB-Dateien steht dann als eigene Nachricht darunter. Die Dateien
 * zeigt die Karte zusätzlich selbst, falls die Nachricht (noch) nicht da ist.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { CheckCircle2, CircleX, Clock, Loader2, Timer } from "lucide-react";

import { authenticationAtom } from "../State";
import {
  BackgroundJob,
  activeBackgroundJobsAtom,
  announceJobFinished,
  backgroundJobsAPI,
  isActiveJob,
} from "../BackgroundJobsAPI";
import { FileResource } from "./FileResource";
import { Button } from "@/components/ui/button";

const POLL_MS = 2000;
const DOTS = 20;

/** Quiet progress: 20 small dots, filled in a muted tone — no bar across the card. */
const ProgressDots = ({ percent, label }: { percent: number; label: string }) => {
  const filled = Math.round((percent / 100) * DOTS);
  return (
    <span
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      className="inline-flex shrink-0 items-center gap-[3px]"
    >
      {Array.from({ length: DOTS }, (_, index) => (
        <span
          key={index}
          aria-hidden
          className={`h-1.5 w-1.5 rounded-full ${
            index < filled ? "bg-muted-foreground/60" : "border border-muted-foreground/25"
          }`}
        />
      ))}
    </span>
  );
};

const seconds = (from?: string | null, to?: string | null) =>
  from ? Math.max(0, Math.round(((to ? new Date(to) : new Date()).getTime() - new Date(from).getTime()) / 1000)) : 0;

/** A running clock: m:ss, or h:mm:ss past the hour. */
export const clock = (total: number) => {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

/**
 * Remaining time of the counted step from its rate so far — null until there is a rate worth
 * extrapolating (at least 15 s and 2 % into the step).
 */
export const remainingSeconds = (
  counter: { label?: string; done: number; total: number; since: string; doneAtSince: number; at: string } | null | undefined,
  now: number = Date.now(),
): number | null => {
  if (!counter || counter.total <= 0) return null;
  const elapsed = (new Date(counter.at).getTime() - new Date(counter.since).getTime()) / 1000;
  const progressed = counter.done - counter.doneAtSince;
  if (elapsed < 15 || progressed <= 0 || counter.done / counter.total < 0.02) return null;
  const rate = progressed / elapsed;
  const sinceLast = Math.max(0, (now - new Date(counter.at).getTime()) / 1000);
  return Math.max(0, Math.round((counter.total - counter.done) / rate - sinceLast));
};

export default function JobResource(properties: { resource: any; stableKey?: string }) {
  const { t } = useTranslation();
  const [authentication] = useAtom<any>(authenticationAtom);
  const [, setActive] = useAtom(activeBackgroundJobsAtom);
  const customer = authentication?.customer;
  // Messages saved before the server kept the job id carry only the title: find the user's job by it.
  const [resolvedJobId, setResolvedJobId] = useState<string | undefined>(properties.resource?.jobId);
  const jobId = resolvedJobId;
  useEffect(() => {
    if (properties.resource?.jobId) {
      setResolvedJobId(properties.resource.jobId);
      return;
    }
    const title = properties.resource?.title;
    if (!customer || !title) return;
    let stopped = false;
    backgroundJobsAPI
      .listJobs(customer)
      .then((jobs) => {
        const match = jobs.find((candidate) => candidate.title === title);
        if (!stopped && match) setResolvedJobId(match._id);
      })
      .catch(() => undefined);
    return () => {
      stopped = true;
    };
  }, [customer, properties.resource?.jobId, properties.resource?.title]);
  const [job, setJob] = useState<BackgroundJob | null>(null);
  const [failedToLoad, setFailedToLoad] = useState(false);
  const [, tick] = useState(0);

  useEffect(() => {
    if (!customer || !jobId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const current = await backgroundJobsAPI.getJob(customer, jobId);
        if (stopped) return;
        setJob(current);
        setFailedToLoad(false);
        if (isActiveJob(current)) {
          setActive((ids) => (ids.includes(jobId) ? ids : [...ids, jobId]));
          timer = setTimeout(load, POLL_MS);
        } else {
          setActive((ids) => ids.filter((id) => id !== jobId));
          announceJobFinished(current);
        }
      } catch {
        if (stopped) return;
        setFailedToLoad(true);
        timer = setTimeout(load, POLL_MS * 3);
      }
    };
    load();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [customer, jobId, setActive]);

  // the elapsed time moves while the job runs
  useEffect(() => {
    if (!isActiveJob(job)) return;
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [job]);

  const title = job?.title || properties.resource?.title || t("backgroundJob.title");
  const status = job?.status || properties.resource?.status || "queued";
  const steps = (job?.progress || []).slice(-3);
  const counter = isActiveJob(job) ? job?.counter : null;
  const percent = counter ? Math.min(100, Math.round((counter.done / counter.total) * 100)) : 0;
  const remaining = remainingSeconds(counter);
  const running = seconds(job?.startedAt || job?.createdAt, isActiveJob(job) ? null : job?.finishedAt);
  const files = (job?.result?.resources || []).filter((resource: any) => resource?.type === "file");

  const cancel = async () => {
    if (!customer || !jobId) return;
    try {
      setJob(await backgroundJobsAPI.cancel(customer, jobId));
    } catch {
      // the next poll shows the state
    }
  };

  const icon =
    status === "done" ? (
      <CheckCircle2 aria-hidden className="h-5 w-5 shrink-0 text-success" />
    ) : status === "failed" || status === "cancelled" ? (
      <CircleX aria-hidden className="h-5 w-5 shrink-0 text-destructive" />
    ) : status === "queued" ? (
      <Timer aria-hidden className="h-5 w-5 shrink-0 text-muted-foreground" />
    ) : (
      <Loader2 aria-hidden className="h-5 w-5 shrink-0 animate-spin text-primary" />
    );

  return (
    <div className="my-2 flex w-full min-w-0 flex-col gap-2 rounded-lg border border-border bg-card p-3" key={properties.stableKey}>
      <div className="flex min-w-0 items-start gap-2">
        {icon}
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="min-w-0 break-words font-medium">{title}</span>
          <span className="min-w-0 break-words text-xs text-muted-foreground">
            {t(`backgroundJob.status.${status}`)}
            {job && job.attempts > 1 && isActiveJob(job) ? ` · ${t("backgroundJob.restarted")}` : ""}
          </span>
        </div>
        {job ? (
          <span
            className="flex shrink-0 items-center gap-1 font-mono text-sm tabular-nums text-muted-foreground"
            aria-label={t("backgroundJob.elapsed")}
            title={t("backgroundJob.elapsed")}
          >
            <Clock aria-hidden className="h-4 w-4" />
            {clock(running)}
          </span>
        ) : null}
        {isActiveJob(job) ? (
          <Button size="sm" variant="outline" onClick={cancel} disabled={!!(job as any)?.cancelRequested}>
            {t("backgroundJob.cancel")}
          </Button>
        ) : null}
      </div>

      {counter ? (
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <ProgressDots percent={percent} label={counter.label} />
          <span className="min-w-0 break-words">{counter.label}</span>
          <span className="tabular-nums">
            {t("backgroundJob.counted", {
              done: counter.done.toLocaleString(),
              total: counter.total.toLocaleString(),
              percent,
            })}
            {remaining !== null ? ` · ${t("backgroundJob.remaining", { time: clock(remaining) })}` : ""}
          </span>
        </div>
      ) : null}

      {isActiveJob(job) && steps.length ? (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-xs text-muted-foreground">
          {steps.map((step, index) => (
            <li key={`${step.at}-${index}`} className="min-w-0 break-words">
              {step.text}
            </li>
          ))}
        </ul>
      ) : null}

      {status === "failed" && job?.error ? (
        <p className="m-0 min-w-0 break-words text-xs text-destructive">{job.error}</p>
      ) : null}

      {status === "done" ? (
        <>
          <p className="m-0 min-w-0 break-words text-xs text-muted-foreground">{t(files.length ? "backgroundJob.doneHint" : "backgroundJob.doneHintNoFiles")}</p>
          {files.length ? (
            <div className="flex min-w-0 flex-col gap-1">
              {files.slice(0, 6).map((file: any, index: number) => (
                <FileResource key={`${jobId}-file-${index}`} resource={file} stableKey={`${jobId}-file-${index}`} />
              ))}
              {files.length > 6 ? (
                <span className="text-xs text-muted-foreground">
                  {t("backgroundJob.moreFiles", { count: files.length - 6 })}
                </span>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {failedToLoad && !job ? (
        <p className="m-0 text-xs text-muted-foreground">{t("backgroundJob.unavailable")}</p>
      ) : null}
    </div>
  );
}
